import { useState, useEffect } from 'react';
import IconSelectInput from '../../../ui/inputs/IconSelectInput';
import { useTranslation } from 'react-i18next';
import AINormalInput from '../../../ui/inputs/AINormalInput';
import '../../../styles/pages/notifications/InsightItemConfig.scss';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import { update_presentation_api_call } from '../../../lib/api/presentations/presentations-api-call';
import {
  Presentation,
  PresentationUpdateRequest,
} from '../../../interfaces/Presentation';
import SettingsIconSelectInput from '../../../ui/inputs/SettingsIconSelectInput';
import ToggleSwitch from '../../../ui/buttons/ToggleSwitch';

type Props = {
  togglePopupModal: (isOpen: boolean) => void;
  onPresentationUpdated?: () => void;
  frequencies?: { [key: string]: string } | null;
  presentation: Presentation;
};

const EditReportsPresentations = ({
  togglePopupModal,
  onPresentationUpdated,
  frequencies,
  presentation,
}: Props) => {
  const { t } = useTranslation();

  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [time, setTime] = useState(presentation.frequency);
  const [presentationURL, setPresentationURL] = useState(presentation.url);
  const [presentationName, setPresentationName] = useState(presentation.name);
  const [presentationDescription, setPresentationDescription] = useState(
    presentation.description
  );
  const [removeEmptySlides, setRemoveEmptySlides] = useState(
    presentation.configs?.remove_empty_slides ?? false
  );

  useEffect(() => {
    const isValid =
      presentationName.trim() !== '' &&
      presentationURL.trim() !== '' &&
      presentationDescription.trim() !== '' &&
      time !== '' &&
      frequencies !== null;
    setIsSaveDisabled(!isValid);
  }, [
    presentationName,
    presentationURL,
    presentationDescription,
    time,
    frequencies,
  ]);

  const handleSave = async () => {
    if (isSaveDisabled || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const presentationData: PresentationUpdateRequest = {
        name: presentationName.trim(),
        url: presentationURL.trim(),
        frequency: time as any,
        description: presentationDescription.trim(),
        is_active: presentation.is_active,
        configs: {
          remove_empty_slides: removeEmptySlides,
        },
      };

      const result = await update_presentation_api_call(
        presentation.id,
        presentationData
      );

      if (result) {
        togglePopupModal(false);
        if (onPresentationUpdated) {
          onPresentationUpdated();
        }
      }
    } catch (error) {
      console.error('Error updating presentation:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFrequencyOptions = () => {
    if (!frequencies) {
      return [];
    }

    return Object.entries(frequencies).map(([key, value]) => ({
      text: value,
      value: key,
      icon: null,
    }));
  };

  return (
    <div className='insightItemConfig'>
      <div className='insightItemConfig__title'>
        <div className='insightItemConfig__title--main'>
          <h3>{t('EditPresentation')}</h3>
        </div>

        <p>{t('UpdateGoogleSlidesURL')}</p>
      </div>

      <div className='insightItemConfig__inputs'>
        <AINormalInput
          changeListeners={[(e) => setPresentationName(e.target.value)]}
          id='presentationName'
          value={presentationName}
          placeholder={t('PresentationName')}
          required={true}
          type='text'
          innerPlaceholder={t('EnterPresentationName')}
        />
        <AINormalInput
          changeListeners={[(e) => setPresentationDescription(e.target.value)]}
          id='presentationDescription'
          value={presentationDescription}
          placeholder={t('PresentationDescription')}
          required={true}
          type='text'
          innerPlaceholder={t('EnterPresentationDescription')}
        />
        <AINormalInput
          changeListeners={[(e) => setPresentationURL(e.target.value)]}
          id='presentationURL'
          value={presentationURL}
          placeholder={t('GoogleSlidesURL')}
          required={true}
          type='text'
          innerPlaceholder={'https://docs.google.com/presentation/d/....'}
        />
        <SettingsIconSelectInput
          filterOptions={getFrequencyOptions()}
          value={time}
          onFilterChange={(val) => setTime(val)}
          marginTop='1.6rem'
          title={t('GenerationFrequency')}
        />
        <div style={{ marginTop: '1.6rem', marginBottom: '1.6rem' }}>
          <ToggleSwitch
            title={t('RemoveEmptySlides')}
            isOn={removeEmptySlides}
            onClick={() => {
              setRemoveEmptySlides(!removeEmptySlides);
            }}
            id='Remove-Empty-Slides-Switch'
            disabled={isSubmitting}
          />
        </div>
      </div>

      <div className='insightItemConfig__buttons'>
        <MainSquareButton
          value={t('Cancel')}
          type='button'
          buttonType='white'
          disabled={isSubmitting}
          onClick={() => togglePopupModal(false)}
        />
        <MainSquareButton
          value={isSubmitting ? t('Updating...') : t('UpdatePresentation')}
          type='button'
          disabled={isSaveDisabled || isSubmitting}
          onClick={handleSave}
        />
      </div>
    </div>
  );
};

export default EditReportsPresentations;
